import type {
  UnifiedFilterMeta,
  UnifiedFilterPickerContext,
  UnifiedFilterValueOption,
} from '@/components/filters/list-filter-types'

/**
 * Facet/static catalogs can omit applied values (stale harvest, narrowed cohort).
 * Chip edit must always show the full picker catalog plus any selected values.
 */
export function mergePickerOptionsWithSelected(
  catalog: UnifiedFilterValueOption[],
  selectedValues: string[],
  labelForValue?: (value: string) => string,
): UnifiedFilterValueOption[] {
  const options = [...catalog]
  const seen = new Set(options.map((option) => option.value))

  for (const value of selectedValues) {
    if (!value || seen.has(value)) continue
    options.push({
      value,
      label: labelForValue?.(value) ?? value,
    })
    seen.add(value)
  }

  return options.sort((a, b) => a.label.localeCompare(b.label))
}

export function resolveUnifiedPickerCatalog<TFilters, TCtx>(
  meta: UnifiedFilterMeta<TFilters, TCtx>,
  ctx: TCtx,
  filters: TFilters,
  picker: UnifiedFilterPickerContext,
): UnifiedFilterValueOption[] {
  const catalog = meta.getPickerOptions?.(ctx, picker) ?? meta.getOptions(ctx)
  const selected = meta.getSelectedValues(filters, ctx)

  if (
    meta.optionsSource === 'faceted' ||
    (meta.selection === 'multi' && picker.origin === 'chip')
  ) {
    const labelByValue = new Map(catalog.map((option) => [option.value, option.label]))
    return mergePickerOptionsWithSelected(
      catalog,
      selected,
      (value) => labelByValue.get(value) ?? value,
    )
  }

  return catalog
}

export function isUnifiedPickerCatalogLoading<TFilters, TCtx>(
  meta: UnifiedFilterMeta<TFilters, TCtx>,
  ctx: TCtx,
  catalog: UnifiedFilterValueOption[],
  filters: TFilters,
): boolean {
  if (!meta.isPickerCatalogLoading?.(ctx)) return false
  if (catalog.length > meta.getSelectedValues(filters, ctx).length) return false
  return true
}
